import React, { useEffect, useMemo, useState } from 'react';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Button,
  Card,
  Stack,
  IconButton,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Delete, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import { Space } from '../../models/Space';
import type { HostRoom } from '../../models/Room';
import { OrganizationType } from '../../models/Organization';
import { useTranslation } from 'react-i18next';

const MySpacesPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [rooms, setRooms] = useState<HostRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markedSpacesForDeletion, setMarkedSpacesForDeletion] = useState<string[]>([]);
  const [markedRoomsForDeletion, setMarkedRoomsForDeletion] = useState<string[]>([]);
  const [visibilityUpdatingId, setVisibilityUpdatingId] = useState<string | null>(null);
  const [isIndividualTutorOrg, setIsIndividualTutorOrg] = useState(false);
  const navigate = useNavigate();

  const spaceMap = useMemo(
    () => new Map(spaces.map((space) => [space.id, space.name])),
    [spaces]
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const organization = await hostApiClient.getOrganization().catch(() => null);
        const individualTutor = organization?.organizationType === OrganizationType.INDIVIDUAL_TUTOR;
        setIsIndividualTutorOrg(individualTutor);

        if (individualTutor) {
          const spaceData = await hostApiClient.getSpaces();
          setSpaces(spaceData);
          setRooms([]);
        } else {
          const [spaceData, roomData] = await Promise.all([
            hostApiClient.getSpaces(),
            hostApiClient.getRooms(),
          ]);
          setSpaces(spaceData);
          setRooms(roomData);
        }
      } catch (err) {
        setError(t('spaces.fetchError', 'Failed to fetch spaces'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const getFullAddress = (space: Space | undefined) => {
    if (!space) return null;
    const parts: string[] = [];
    if (space.addressLine1) parts.push(space.addressLine1);
    if (space.addressLine2) parts.push(space.addressLine2);
    if (space.city) parts.push(space.city);
    if (space.state) parts.push(space.state);
    if (space.zipCode) parts.push(space.zipCode);
    return parts.join(', ').trim();
  };

  const toggleMarkSpaceForDeletion = (id: string) => {
    setMarkedSpacesForDeletion((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleMarkRoomForDeletion = (id: string) => {
    setMarkedRoomsForDeletion((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSaveSpaceDeletion = async () => {
    try {
      for (const id of markedSpacesForDeletion) {
        await hostApiClient.deleteSpace(String(id));
      }
      setSpaces((prev) => prev.filter((space) => !markedSpacesForDeletion.includes(space.id)));
      setMarkedSpacesForDeletion([]);
    } catch (err) {
      console.error('Failed to delete some spaces:', err);
      setError(t('spaces.deletionError', 'Some deletions failed.'));
    }
  };

  const handleSaveRoomDeletion = async () => {
    try {
      for (const id of markedRoomsForDeletion) {
        await hostApiClient.deleteRoom(String(id));
      }
      setRooms((prev) => prev.filter((room) => !markedRoomsForDeletion.includes(room.id)));
      setMarkedRoomsForDeletion([]);
    } catch (err) {
      console.error(t('rooms.deletionError', 'Failed to delete some rooms:'), err);
      setError(t('rooms.deletionError', 'Some deletions failed.'));
    }
  };

  const handleRoomVisibilityToggle = async (roomId: string, visible: boolean) => {
    const previous = rooms;
    setRooms((prev) =>
      prev.map((room) => (room.id === roomId ? { ...room, visible } : room)),
    );
    setVisibilityUpdatingId(roomId);
    setError(null);
    try {
      await hostApiClient.updateRoomVisibility(roomId, { visible });
    } catch (err) {
      setRooms(previous);
      console.error(t('rooms.visibilityUpdateFailed', 'Could not update room visibility.'), err);
      setError(t('rooms.visibilityUpdateFailed', 'Could not update room visibility.'));
    } finally {
      setVisibilityUpdatingId(null);
    }
  };

  const spaceColumns: GridColDef[] = [
    { field: 'name', headerName: t('spaces.name', 'Name'), flex: 2 },
    {
      field: 'address',
      headerName: t('spaces.address', 'Address'),
      flex: 2,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {getFullAddress(params.row) || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          color={markedSpacesForDeletion.includes(params.row.id) ? 'warning' : 'error'}
          onClick={(e) => {
            e.stopPropagation();
            toggleMarkSpaceForDeletion(params.row.id);
          }}
        >
          <Delete />
        </IconButton>
      ),
    },
  ];

  const roomColumns: GridColDef[] = [
    { field: 'name', headerName: t('rooms.name', 'Name'), flex: 2 },
    { field: 'spaceName', headerName: t('rooms.space', 'Space'), flex: 2 },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const roomVisible = Boolean(params.row.visible);
        const visibilityBusy = visibilityUpdatingId === params.row.id;
        const visibilityLabel = roomVisible
          ? t('rooms.hideRoom', 'Hide from public booking')
          : t('rooms.showRoom', 'Show on public booking');

        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title={visibilityLabel}>
              <span>
                <IconButton
                  size="small"
                  disabled={visibilityBusy}
                  color={roomVisible ? 'primary' : 'default'}
                  aria-label={`${t('rooms.publicVisibilityFor', 'Public visibility for')} ${params.row.name}`}
                  aria-pressed={roomVisible}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleRoomVisibilityToggle(params.row.id, !roomVisible);
                  }}
                  sx={{ color: roomVisible ? 'primary.main' : 'text.disabled' }}
                >
                  {roomVisible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton
              color={markedRoomsForDeletion.includes(params.row.id) ? 'warning' : 'error'}
              onClick={(e) => {
                e.stopPropagation();
                toggleMarkRoomForDeletion(params.row.id);
              }}
            >
              <Delete />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  const displayedSpaces = spaces.map((space) =>
    markedSpacesForDeletion.includes(space.id)
      ? { ...space, name: `[${t('spaces.toBeDeleted', 'To be deleted')}] ${space.name}` }
      : space
  );

  const displayedRooms = useMemo(() => {
    return rooms.map((room) =>
      markedRoomsForDeletion.includes(room.id)
        ? {
            ...room,
            name: `[${t('rooms.toBeDeleted', 'To be deleted')}] ${room.name}`,
            spaceName: spaceMap.get(room.spaceId) || '—',
          }
        : {
            ...room,
            spaceName: spaceMap.get(room.spaceId) || '—',
          }
    );
  }, [rooms, markedRoomsForDeletion, spaceMap, t]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1000, width: '100%', mx: 'auto' }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {isIndividualTutorOrg
          ? t('navigation.spaces', 'Spaces')
          : t('spaces.spacesAndRoomsTitle', 'Spaces and Rooms')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: isIndividualTutorOrg ? 0 : 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="h2">{t('spaces.title', 'My Spaces')}</Typography>
          <Button variant="contained" onClick={() => navigate('/host/spaces/create')}>
            {t('spaces.add', 'Add')}
          </Button>
        </Stack>
        <Divider sx={{ mb: { xs: 2, md: 3 } }} />

        {isMobile ? (
          <>
            {spaces.length === 0 ? (
              <Box
                sx={{
                  mt: 4,
                  mb: 4,
                  textAlign: 'center',
                  py: 6,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {t('spaces.noSpaces', 'No spaces found')}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/host/spaces/create')}
                  sx={{ mt: 2 }}
                >
                  {t('spaces.createFirst', 'Create your first space')}
                </Button>
              </Box>
            ) : (
              <Box sx={{ mt: 2, mx: -1 }}>
                {displayedSpaces.map((space) => {
                  const isMarked = markedSpacesForDeletion.includes(space.id);
                  return (
                    <Box
                      key={space.id}
                      sx={{
                        mb: 1,
                        p: 2,
                        border: '1px solid',
                        borderColor: isMarked ? 'warning.main' : 'divider',
                        borderRadius: 2,
                        bgcolor: isMarked ? 'rgba(237, 108, 2, 0.08)' : 'background.paper',
                        cursor: isMarked ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: isMarked ? '0 2px 8px rgba(237, 108, 2, 0.15)' : 'none',
                        '&:active': {
                          transform: isMarked ? 'none' : 'scale(0.98)',
                        },
                      }}
                      onClick={() => !isMarked && navigate(`/host/spaces/${space.id}`)}
                    >
                      <Box>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={2}
                          sx={{ mb: 1 }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              fontSize: '1.125rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              letterSpacing: '-0.01em',
                              color: isMarked ? 'text.secondary' : 'text.primary',
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {space.name}
                          </Typography>
                          <IconButton
                            size="medium"
                            color={isMarked ? 'warning' : 'error'}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleMarkSpaceForDeletion(space.id);
                            }}
                            sx={{
                              bgcolor: isMarked
                                ? 'rgba(237, 108, 2, 0.15)'
                                : 'rgba(211, 47, 47, 0.08)',
                              '&:hover': {
                                bgcolor: isMarked
                                  ? 'rgba(237, 108, 2, 0.25)'
                                  : 'rgba(211, 47, 47, 0.15)',
                              },
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Stack>

                        <Divider sx={{ mb: 1 }} />

                        {getFullAddress(space) && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.875rem',
                              mb: isMarked ? 0.75 : 0,
                              lineHeight: 1.4,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {getFullAddress(space)}
                          </Typography>
                        )}
                        {isMarked && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              color: 'warning.main',
                              fontWeight: 600,
                              bgcolor: 'rgba(237, 108, 2, 0.12)',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              mt: 0.5,
                            }}
                          >
                            {t('spaces.markedForDeletion', 'Marked for deletion')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        ) : (
          <DataGrid
            rows={displayedSpaces}
            columns={spaceColumns}
            onRowClick={(params) => {
              if (markedSpacesForDeletion.includes(params.id as string)) return;
              navigate(`/host/spaces/${params.id}`);
            }}
            hideFooter
            autoHeight
            sx={{
              mt: 4,
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-columnHeader': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: 'action.selected',
              },
            }}
          />
        )}

        {markedSpacesForDeletion.length > 0 && (
          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => setMarkedSpacesForDeletion([])}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="contained" color="error" onClick={handleSaveSpaceDeletion}>
              {t('spaces.deleteSelected', 'Delete Selected')} ({markedSpacesForDeletion.length})
            </Button>
          </Box>
        )}
      </Card>

      {!isIndividualTutorOrg ? (
      <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="h2">{t('rooms.title', 'My Rooms')}</Typography>
          <Button variant="contained" onClick={() => navigate('/host/rooms/create')}>
            {t('rooms.add', 'Add')}
          </Button>
        </Stack>
        <Divider sx={{ mb: { xs: 2, md: 3 } }} />

        {isMobile ? (
          <>
            {rooms.length === 0 ? (
              <Box
                sx={{
                  mt: 4,
                  mb: 4,
                  textAlign: 'center',
                  py: 6,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {t('rooms.noRooms', 'No rooms found')}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/host/rooms/create')}
                  sx={{ mt: 2 }}
                >
                  {t('rooms.createFirst', 'Create your first room')}
                </Button>
              </Box>
            ) : (
              <Box sx={{ mt: 2, mx: -1 }}>
                {displayedRooms.map((room) => {
                  const isMarked = markedRoomsForDeletion.includes(room.id);
                  const roomVisible = Boolean(room.visible);
                  const visibilityBusy = visibilityUpdatingId === room.id;
                  const visibilityLabel = roomVisible
                    ? t('rooms.hideRoom', 'Hide from public booking')
                    : t('rooms.showRoom', 'Show on public booking');
                  return (
                    <Box
                      key={room.id}
                      sx={{
                        mb: 1,
                        p: 2,
                        border: '1px solid',
                        borderColor: isMarked ? 'warning.main' : 'divider',
                        borderRadius: 2,
                        bgcolor: isMarked ? 'rgba(237, 108, 2, 0.08)' : 'background.paper',
                        cursor: isMarked ? 'default' : 'pointer',
                        opacity: !roomVisible && !isMarked ? 0.72 : 1,
                        transition: 'all 0.2s ease',
                        boxShadow: isMarked ? '0 2px 8px rgba(237, 108, 2, 0.15)' : 'none',
                        '&:active': {
                          transform: isMarked ? 'none' : 'scale(0.98)',
                        },
                      }}
                      onClick={() => !isMarked && navigate(`/host/rooms/${room.id}`)}
                    >
                      <Box>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ mb: 1 }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              fontSize: '1.125rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              letterSpacing: '-0.01em',
                              color: isMarked ? 'text.secondary' : 'text.primary',
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {room.name}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
                            <Tooltip title={visibilityLabel}>
                              <span>
                                <IconButton
                                  size="medium"
                                  disabled={visibilityBusy}
                                  color={roomVisible ? 'primary' : 'default'}
                                  aria-label={`${t('rooms.publicVisibilityFor', 'Public visibility for')} ${room.name}`}
                                  aria-pressed={roomVisible}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleRoomVisibilityToggle(room.id, !roomVisible);
                                  }}
                                  sx={{
                                    color: roomVisible ? 'primary.main' : 'text.disabled',
                                    bgcolor: roomVisible
                                      ? 'rgba(25, 118, 210, 0.08)'
                                      : 'rgba(255, 255, 255, 0.04)',
                                    '&:hover': {
                                      bgcolor: roomVisible
                                        ? 'rgba(25, 118, 210, 0.15)'
                                        : 'rgba(255, 255, 255, 0.08)',
                                    },
                                  }}
                                >
                                  {roomVisible ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <IconButton
                              size="medium"
                              color={isMarked ? 'warning' : 'error'}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMarkRoomForDeletion(room.id);
                              }}
                              sx={{
                                bgcolor: isMarked
                                  ? 'rgba(237, 108, 2, 0.15)'
                                  : 'rgba(211, 47, 47, 0.08)',
                                '&:hover': {
                                  bgcolor: isMarked
                                    ? 'rgba(237, 108, 2, 0.25)'
                                    : 'rgba(211, 47, 47, 0.15)',
                                },
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Stack>
                        </Stack>

                        <Divider sx={{ mb: 1 }} />

                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                            mb: isMarked ? 0.75 : 0,
                            lineHeight: 1.4,
                          }}
                        >
                          {room.spaceName}
                        </Typography>
                        {isMarked && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              color: 'warning.main',
                              fontWeight: 600,
                              bgcolor: 'rgba(237, 108, 2, 0.12)',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              mt: 0.5,
                            }}
                          >
                            {t('rooms.markedForDeletion', 'Marked for deletion')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        ) : (
          <DataGrid
            rows={displayedRooms}
            columns={roomColumns}
            onRowClick={(params) => {
              if (markedRoomsForDeletion.includes(params.id as string)) return;
              navigate(`/host/rooms/${params.id}`);
            }}
            getRowClassName={(params) =>
              params.row.visible === false ? 'room-row-hidden' : ''
            }
            hideFooter
            autoHeight
            sx={{
              mt: 4,
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-columnHeader': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: 'action.selected',
              },
              '& .MuiDataGrid-row.room-row-hidden': {
                opacity: 0.72,
              },
            }}
          />
        )}

        {markedRoomsForDeletion.length > 0 && (
          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => setMarkedRoomsForDeletion([])}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="contained" color="error" onClick={handleSaveRoomDeletion}>
              {t('rooms.deleteSelected', 'Delete Selected')} ({markedRoomsForDeletion.length})
            </Button>
          </Box>
        )}
      </Card>
      ) : null}
    </Box>
  );
};

export default MySpacesPage;
